import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  RefreshCw,
  Globe,
  DollarSign,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { Input, Dropdown, NumberInput, TimePicker } from '../components/forms';
// import LoadingSpinner from '../components/LoadingSpinner'; // Unused import
// import SkeletonLoader from '../components/SkeletonLoader'; // Unused import

interface SystemConfig {
  // Regional Settings
  defaultTimezone: string;
  defaultLanguage: string;
  defaultCountry: string;

  // Currency Settings
  baseCurrency: string;
  currencySymbol: string;
  currencyPosition: 'before' | 'after';
  decimalPlaces: number;
  thousandsSeparator: string;
  decimalSeparator: string;

  // Date & Time Formats
  dateFormat: string;
  timeFormat: '12h' | '24h';
  firstDayOfWeek: 'sunday' | 'monday';
  dateLocale: string;

  // Number Formats
  numberFormat: string;

  // Business Settings
  businessHoursStart: string;
  businessHoursEnd: string;
  workingDays: string[];
  fiscalYearStart: string;

  // Security Settings
  sessionTimeout: number;
  passwordMinLength: number;
  requireTwoFactor: boolean;

  // Email Settings
  defaultFromEmail: string;
  defaultFromName: string;
  emailSignature: string;

  // System Settings
  maintenanceMode: boolean;
  systemName: string;
  systemDescription: string;
  maxFileUploadSize: number;
  allowedFileTypes: string[];
}

const defaultSystemConfig: SystemConfig = {
  defaultTimezone: 'America/New_York',
  defaultLanguage: 'en',
  defaultCountry: 'US',
  baseCurrency: 'USD',
  currencySymbol: '$',
  currencyPosition: 'before',
  decimalPlaces: 2,
  thousandsSeparator: ',',
  decimalSeparator: '.',
  dateFormat: 'MM/dd/yyyy',
  timeFormat: '12h',
  firstDayOfWeek: 'sunday',
  dateLocale: 'en-US',
  numberFormat: 'en-US',
  businessHoursStart: '09:00',
  businessHoursEnd: '17:00',
  workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  fiscalYearStart: 'january',
  sessionTimeout: 60,
  passwordMinLength: 8,
  requireTwoFactor: false,
  defaultFromEmail: 'noreply@example.com',
  defaultFromName: 'builderbel System',
  emailSignature: '',
  maintenanceMode: false,
  systemName: 'builderbel',
  systemDescription: 'Construction Management Platform',
  maxFileUploadSize: 50,
  allowedFileTypes: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif'],
};

const SystemSettings: React.FC = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<SystemConfig>(defaultSystemConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState('regional');

  // Options for dropdowns
  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (UTC-5)' },
    { value: 'America/Chicago', label: 'Central Time (UTC-6)' },
    { value: 'America/Denver', label: 'Mountain Time (UTC-7)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (UTC-8)' },
    { value: 'UTC', label: 'UTC (UTC+0)' },
    { value: 'Europe/London', label: 'London (UTC+0)' },
    { value: 'Europe/Paris', label: 'Paris (UTC+1)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
    { value: 'Australia/Sydney', label: 'Sydney (UTC+10)' },
  ];

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' },
  ];

  const countries = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'FR', label: 'France' },
    { value: 'DE', label: 'Germany' },
    { value: 'ES', label: 'Spain' },
    { value: 'IT', label: 'Italy' },
    { value: 'AU', label: 'Australia' },
  ];

  const currencies = [
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'GBP', label: 'British Pound (£)' },
    { value: 'CAD', label: 'Canadian Dollar (C$)' },
    { value: 'AUD', label: 'Australian Dollar (A$)' },
  ];

  const dateFormats = [
    { value: 'MM/dd/yyyy', label: 'MM/DD/YYYY (US)' },
    { value: 'dd/MM/yyyy', label: 'DD/MM/YYYY (UK)' },
    { value: 'yyyy-MM-dd', label: 'YYYY-MM-DD (ISO)' },
    { value: 'dd.MM.yyyy', label: 'DD.MM.YYYY (German)' },
  ];

  const workingDaysOptions = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' },
  ];

  const tabs = [
    { id: 'regional', name: 'Regional', icon: Globe },
    { id: 'currency', name: 'Currency', icon: DollarSign },
    { id: 'datetime', name: 'Date & Time', icon: Calendar },
    { id: 'business', name: 'Business', icon: Clock },
  ];

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      // In production, this would fetch from API
      const savedConfig = localStorage.getItem('systemConfig');
      if (savedConfig) {
        setConfig({ ...defaultSystemConfig, ...JSON.parse(savedConfig) });
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      setMessage({ type: 'error', text: 'Failed to load configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors({});

    try {
      // Validate configuration
      const validationErrors: Record<string, string> = {};

      if (!config.defaultTimezone) {
        validationErrors.defaultTimezone = 'Default timezone is required';
      }

      if (!config.baseCurrency) {
        validationErrors.baseCurrency = 'Base currency is required';
      }

      if (config.decimalPlaces < 0 || config.decimalPlaces > 4) {
        validationErrors.decimalPlaces = 'Decimal places must be between 0 and 4';
      }

      if (config.sessionTimeout < 5 || config.sessionTimeout > 480) {
        validationErrors.sessionTimeout = 'Session timeout must be between 5 and 480 minutes';
      }

      if (config.passwordMinLength < 6 || config.passwordMinLength > 128) {
        validationErrors.passwordMinLength =
          'Password minimum length must be between 6 and 128 characters';
      }

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setMessage({ type: 'error', text: 'Please fix the validation errors' });
        return;
      }

      // Save to localStorage (in production, this would be an API call)
      localStorage.setItem('systemConfig', JSON.stringify(config));

      setMessage({ type: 'success', text: 'System configuration saved successfully!' });

      // Auto-hide success message
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving configuration:', error);
      setMessage({ type: 'error', text: 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (confirm('Are you sure you want to reset all settings to default values?')) {
      setConfig(defaultSystemConfig);
      localStorage.removeItem('systemConfig');
      setMessage({ type: 'success', text: 'Configuration reset to defaults' });
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const updateConfig = (field: keyof SystemConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleWorkingDaysChange = (day: string, checked: boolean) => {
    setConfig(prev => ({
      ...prev,
      workingDays: checked ? [...prev.workingDays, day] : prev.workingDays.filter(d => d !== day),
    }));
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-2">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 bg-gray-200 rounded"></div>
              <div>
                <div className="h-6 bg-gray-200 rounded w-48 mb-1"></div>
                <div className="h-4 bg-gray-200 rounded w-64"></div>
              </div>
            </div>
            <div className="flex space-x-2">
              <div className="h-8 bg-gray-200 rounded w-16"></div>
              <div className="h-8 bg-gray-200 rounded w-16"></div>
            </div>
          </div>
          <div className="border-b border-gray-200 mb-6">
            <div className="flex space-x-8">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-8 bg-gray-200 rounded w-20"></div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="space-y-6">
              <div className="h-6 bg-gray-200 rounded w-32"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-10 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/dashboard/settings')}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">System Configuration</h1>
            <p className="text-sm text-gray-600">Configure system-wide settings and preferences</p>
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={handleReset}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Status Message */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-md ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-red-400 text-red-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="mr-2 h-4 w-4" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {activeTab === 'regional' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Regional Settings</h3>
              <p className="text-sm text-gray-600 mb-6">
                Configure default regional preferences for the system
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Dropdown
                label="Default Timezone"
                options={timezones}
                value={config.defaultTimezone}
                onChange={value => updateConfig('defaultTimezone', value)}
                error={errors.defaultTimezone}
                hint="Default timezone for new users and system operations"
                required
              />

              <Dropdown
                label="Default Language"
                options={languages}
                value={config.defaultLanguage}
                onChange={value => updateConfig('defaultLanguage', value)}
                hint="Default language for the interface"
                required
              />

              <Dropdown
                label="Default Country"
                options={countries}
                value={config.defaultCountry}
                onChange={value => updateConfig('defaultCountry', value)}
                hint="Default country for new users"
                required
              />

              <Dropdown
                label="Number Format Locale"
                options={[
                  { value: 'en-US', label: 'US English (1,234.56)' },
                  { value: 'en-GB', label: 'UK English (1,234.56)' },
                  { value: 'fr-FR', label: 'French (1 234,56)' },
                  { value: 'de-DE', label: 'German (1.234,56)' },
                  { value: 'es-ES', label: 'Spanish (1.234,56)' },
                ]}
                value={config.numberFormat}
                onChange={value => updateConfig('numberFormat', value)}
                hint="How numbers are formatted throughout the system"
              />
            </div>

            <div>
              <Input
                label="System Name"
                value={config.systemName}
                onChange={e => updateConfig('systemName', e.target.value)}
                placeholder="builderbel"
                hint="Name displayed in the application header and emails"
              />
            </div>

            <div>
              <Input
                label="System Description"
                value={config.systemDescription}
                onChange={e => updateConfig('systemDescription', e.target.value)}
                placeholder="Construction Management Platform"
                hint="Brief description of the system"
              />
            </div>
          </div>
        )}

        {activeTab === 'currency' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Currency Settings</h3>
              <p className="text-sm text-gray-600 mb-6">
                Configure how currency is displayed and calculated
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Dropdown
                label="Base Currency"
                options={currencies}
                value={config.baseCurrency}
                onChange={value => {
                  updateConfig('baseCurrency', value);
                  // Auto-update symbol
                  const currency = currencies.find(c => c.value === value);
                  if (currency) {
                    const symbol =
                      value === 'USD' ? '$' : value === 'EUR' ? '€' : value === 'GBP' ? '£' : '$';
                    updateConfig('currencySymbol', symbol);
                  }
                }}
                error={errors.baseCurrency}
                required
              />

              <Input
                label="Currency Symbol"
                value={config.currencySymbol}
                onChange={e => updateConfig('currencySymbol', e.target.value)}
                placeholder="$"
                hint="Symbol displayed with currency amounts"
              />

              <Dropdown
                label="Symbol Position"
                options={[
                  { value: 'before', label: 'Before amount ($100)' },
                  { value: 'after', label: 'After amount (100$)' },
                ]}
                value={config.currencyPosition}
                onChange={value => updateConfig('currencyPosition', value)}
              />

              <NumberInput
                label="Decimal Places"
                value={config.decimalPlaces}
                onChange={value => updateConfig('decimalPlaces', value)}
                min={0}
                max={4}
                allowNegative={false}
                error={errors.decimalPlaces}
                hint="Number of decimal places for currency display (0-4)"
              />

              <Input
                label="Thousands Separator"
                value={config.thousandsSeparator}
                onChange={e => updateConfig('thousandsSeparator', e.target.value)}
                placeholder=","
                hint="Character used to separate thousands"
              />

              <Input
                label="Decimal Separator"
                value={config.decimalSeparator}
                onChange={e => updateConfig('decimalSeparator', e.target.value)}
                placeholder="."
                hint="Character used to separate decimals"
              />
            </div>
          </div>
        )}

        {activeTab === 'datetime' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Date & Time Settings</h3>
              <p className="text-sm text-gray-600 mb-6">
                Configure how dates and times are displayed
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Dropdown
                label="Date Format"
                options={dateFormats}
                value={config.dateFormat}
                onChange={value => updateConfig('dateFormat', value)}
                hint="How dates are displayed throughout the system"
              />

              <Dropdown
                label="Time Format"
                options={[
                  { value: '12h', label: '12 Hour (1:30 PM)' },
                  { value: '24h', label: '24 Hour (13:30)' },
                ]}
                value={config.timeFormat}
                onChange={value => updateConfig('timeFormat', value)}
              />

              <Dropdown
                label="First Day of Week"
                options={[
                  { value: 'sunday', label: 'Sunday' },
                  { value: 'monday', label: 'Monday' },
                ]}
                value={config.firstDayOfWeek}
                onChange={value => updateConfig('firstDayOfWeek', value)}
                hint="First day of the week in calendars"
              />

              <Dropdown
                label="Date Locale"
                options={[
                  { value: 'en-US', label: 'US English' },
                  { value: 'en-GB', label: 'UK English' },
                  { value: 'fr-FR', label: 'French' },
                  { value: 'de-DE', label: 'German' },
                  { value: 'es-ES', label: 'Spanish' },
                ]}
                value={config.dateLocale}
                onChange={value => updateConfig('dateLocale', value)}
                hint="Locale for date formatting"
              />
            </div>
          </div>
        )}

        {activeTab === 'business' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Settings</h3>
              <p className="text-sm text-gray-600 mb-6">
                Configure business hours and operational preferences
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TimePicker
                label="Business Hours Start"
                value={config.businessHoursStart}
                onChange={val => updateConfig('businessHoursStart', val)}
                hint="When business hours begin each day"
              />

              <TimePicker
                label="Business Hours End"
                value={config.businessHoursEnd}
                onChange={val => updateConfig('businessHoursEnd', val)}
                hint="When business hours end each day"
              />

              <Dropdown
                label="Fiscal Year Start"
                options={[
                  { value: 'january', label: 'January' },
                  { value: 'april', label: 'April' },
                  { value: 'july', label: 'July' },
                  { value: 'october', label: 'October' },
                ]}
                value={config.fiscalYearStart}
                onChange={value => updateConfig('fiscalYearStart', value)}
                hint="When the fiscal year begins"
              />

              <NumberInput
                label="Session Timeout (minutes)"
                value={config.sessionTimeout}
                onChange={value => updateConfig('sessionTimeout', value)}
                min={5}
                max={480}
                allowNegative={false}
                error={errors.sessionTimeout}
                hint="How long users stay logged in when inactive"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Working Days</label>
              <div className="grid grid-cols-4 gap-3">
                {workingDaysOptions.map(day => (
                  <label key={day.value} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={config.workingDays.includes(day.value)}
                      onChange={e => handleWorkingDaysChange(day.value, e.target.checked)}
                      className="rounded border-gray-300 text-red-500 focus:ring-red-400"
                    />
                    <span className="text-sm text-gray-700">{day.label}</span>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-sm text-gray-500">Days when business operations are active</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemSettings;
